import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PracticeActivityHttpModel,
  PracticeActivityQuery,
  PracticeActivityService,
  PracticeActivityStore,
} from '@examdojo/practice-activity';
import { QuestionAttemptEntityQuery, QuestionAttemptEntityService } from '@examdojo/question';
import { RealtimeChangesListenEvent, RealtimeService } from '@examdojo/supabase';
import { UserQuery } from '@examdojo/user';
import { concatMap, filter, map, merge, of, switchMap, withLatestFrom } from 'rxjs';
import { TopicPracticeDialogService } from '../features/practice/topic-practice/dialog/topic-practice-dialog.service';
import { ExamdojoPlatformService } from '../shared/platform.service';

@Injectable()
export class PracticeActivityRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly userQuery: UserQuery,
    private readonly store: PracticeActivityStore,
    private readonly query: PracticeActivityQuery,
    private readonly practiceActivityService: PracticeActivityService,
    private readonly questionAttemptService: QuestionAttemptEntityService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly platformService: ExamdojoPlatformService,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
  ) {
    merge(this.listenForPracticeActivityInProgressStatusChange()).pipe(takeUntilDestroyed()).subscribe();
  }

  private listenForPracticeActivityInProgressStatusChange() {
    return this.userQuery.active$.pipe(
      filter(Boolean),
      switchMap((user) =>
        this.realtimeService.listenBroadcastRealtime<PracticeActivityHttpModel>(
          [{ event: RealtimeChangesListenEvent.UPDATE }],
          `practice_activity_status:${user.id}`,
        ),
      ),
      withLatestFrom(this.platformService.activeStatusState$),
      filter(([_, isActive]) => isActive),
      map(([event]) => event),
      concatMap((payload) => {
        const record = payload?.payload?.record;
        if (payload.event !== RealtimeChangesListenEvent.UPDATE || !record?.id) {
          return of(null);
        }

        if (this.isStaleUpdate(record.id, record.updated_at)) {
          return of(null);
        }

        if (record.status === 'IN_PROGRESS' && this.questionAttemptQuery.getActiveId() !== record.question_attempt_id) {
          return this.handleInProgressPracticeActivityStatusChange(record);
        }

        if (
          (record.status === 'IN_PROGRESS' || record.status === 'NOT_STARTED') &&
          this.query.getEntity(record.id)?.status !== record.status
        ) {
          return this.handleInProgressPracticeActivityStatusChange(record);
        }

        if (
          this.store.hasEntity(record.id) &&
          ((record.status === 'COMPLETED' && this.query.getEntity(record.id)?.status !== 'COMPLETED') ||
            (record.status === 'DISCARDED' && this.query.getEntity(record.id)?.status !== 'DISCARDED'))
        ) {
          return this.handleDiscardedPracticeActivityStatusChange(record);
        }

        return of(null);
      }),
    );
  }

  private handleInProgressPracticeActivityStatusChange(practiceActivityHttpModel: PracticeActivityHttpModel) {
    this.store.upsert(practiceActivityHttpModel.id, practiceActivityHttpModel);
    return of(null);
  }

  // rejects updates where local record is newer than incoming (handles out-of-order realtime events)
  private isStaleUpdate(activityId: string, incomingUpdatedAt: string): boolean {
    const currentUpdatedAt = this.query.getEntity(activityId)?.updated_at;
    if (!currentUpdatedAt) {
      return false;
    }
    return new Date(currentUpdatedAt) >= new Date(incomingUpdatedAt);
  }

  // private handleCompletedPracticeActivityStatusChange(practiceActivityHttpModel: PracticeActivityHttpModel) {}

  private handleDiscardedPracticeActivityStatusChange(practiceActivityHttpModel: PracticeActivityHttpModel) {
    // Do not update the store if the practice activity is being created. It is happening, when current client triggered the creation of the practice activity.
    if (this.practiceActivityService.creatingPracticeActivity$$.value) {
      return of(null);
    }
    this.questionAttemptService.resetStore();
    this.store.update(practiceActivityHttpModel.id, practiceActivityHttpModel);
    this.store.setCandidates(null);

    // only dismiss if the dialog is actually open (user is on the practice screen)
    if (this.topicPracticeDialogService.isOpen()) {
      return this.topicPracticeDialogService.dismissPracticeActivityDialog();
    }

    return of(null);
  }
}
